import { useRef, useCallback, useState, useEffect } from 'preact/hooks';
import { useAnimation, UseAnimationConfig } from '../UNSAFE_useAnimation';
import { RefObject } from 'preact';

type AnimationCallback = UseAnimationConfig<AnimationStatus, HTMLElement>['onAnimationEnd'];
type AnimationStates = UseAnimationConfig<AnimationStatus, HTMLElement>['animationStates'];

/**
 * Animation Status
 * 'unmounted' indicates the element is unmounted from the screen. No transitions should be applied.
 * 'initial' indicates the element is mounted, but stays hidden and is in the initial state
 * 'opening' indicates the element is in the opening state, visible and begins transitioning in.
 * 'closing' indicates the element is in the closing state, visible and begins transitioning out
 * The animation state lifecycle is as follows: 'unmounted' => 'initial' => 'opening' => 'closing' => 'unmounted'
 */
export type AnimationStatus = 'unmounted' | 'initial' | 'opening' | 'closing';

/**
 * The AnimationStatus hook properties
 */
export type AnimationStatusProps = {
  /**
   * isOpen property indicates that animation should change to status opening
   */
  isOpen: boolean;
  /**
   * If isAnimatedOnMount true, the animation should start on mount
   */
  isAnimatedOnMount: boolean;
  /**
   * animationStates is the configuration of the animations, how the element should be animated
   */
  animationStates: AnimationStates;
  /**
   * The callback called after animation is finished
   * @returns
   */
  onTransitionEnd?: (isOpen: boolean) => void;
};

export function useAnimationStatus<E extends HTMLElement>({
  isOpen,
  isAnimatedOnMount,
  animationStates,
  onTransitionEnd
}: AnimationStatusProps): {
  /**
   * status is the animation status
   */
  status: AnimationStatus;
  /**
   * animationElementRef is the reference of the element that is animated
   */
  animationElementRef: RefObject<E | null>;
  /**
   * setAnimationElementRef is a callback that should be used to set up element for the animation
   * @param node an element that animation should be applied to
   * @returns
   */
  setAnimationElementRef: (node: E | null) => void;
} {
  const [status, setStatus] = useState<AnimationStatus>(isOpen ? 'initial' : 'unmounted');
  const animationElementRef = useRef<E | null>(null);
  const [animationElement, _setAnimationElement] = useState<E | null>(null);

  const { nodeRef } = useAnimation<AnimationStatus, HTMLElement>(status, {
    animationStates,
    isAnimatedOnMount: isAnimatedOnMount,
    onAnimationEnd: useCallback<NonNullable<AnimationCallback>>(() => {
      if (status === 'opening') {
        onTransitionEnd?.(true);
      } else if (!isOpen) {
        Object.assign(animationElement!.style, {
          visibility: null
        });
        setStatus('unmounted');
        onTransitionEnd?.(false);
      }
    }, [status, isOpen, onTransitionEnd, animationElement])
  });

  // We need setRef callback in order to set up visibility and node for the animation
  // before it gets rendered in the next render cycle, i.e. when status === 'initial' the component is rendered,
  // after component is rendered but is not yet painted references are applied, here we have the chance to set visibility to hidden
  // and set up animation node.
  const setAnimationRef = useCallback(
    (node: E | null) => {
      if (animationElementRef.current !== node) {
        animationElementRef.current = node;
        // Component will be visually hidden when status is 'initial' to avoid flickering.
        // Flickering happens because it gets rendered before animation happens
        // in next render cycle
        if (node != null && status == 'initial') {
          Object.assign(node.style, {
            visibility: 'hidden'
          });
        }
        nodeRef(node);
        _setAnimationElement(node);
      }
    },
    [status, nodeRef]
  );

  useEffect(() => {
    // 1. Ignore status update on initial render
    if (status === 'unmounted' && !isOpen) {
      return;
    }
    // 2. Mount visually hidden component
    // Component gets mounted
    if (status === 'unmounted' && isOpen) {
      setStatus('initial');
      return;
    }

    // 3. Setup animation
    // Component has been mounted.
    // We will kick out animation as node is already present in DOM.
    if (status === 'initial' && isOpen) {
      setStatus('opening');
      return;
    }

    // 4. Unhide component.
    if (status === 'opening' && isOpen) {
      Object.assign(animationElement!.style, {
        visibility: 'visible'
      });
      return;
    }

    // 5. Closing
    if (!isOpen) {
      setStatus('closing');
    }
  }, [isOpen, animationElement, status]);

  return {
    status,
    animationElementRef,
    setAnimationElementRef: setAnimationRef
  };
}
